import { useMemo } from "react";
import { useDebounce } from "../../utils/hooks/useDebounce";
import Paper from "@mui/material/Paper";
import SalesFilter from "./Filter";
import TransactionsTable from "./TransactionsTable";
import { toTransactionListFilter } from "./salesListFilters";
import { useSalesListUrlState } from "./useSalesListUrlState";
import { COMPACT_QUERY } from "../../utils/hooks/useCompact";

const compact = "@media " + COMPACT_QUERY;

/** How long a typed filter waits for the next keystroke before it is searched. */
const SEARCH_DEBOUNCE_MS = 500;

interface SalesProps {
  triggerFetch: number;
}

export default function Sales({ triggerFetch }: SalesProps) {
  const { filters, page, limit, listSearch, changeFilters, setPage, setLimit } =
    useSalesListUrlState();
  const { dateFrom, dateTo, companyId, routeCode, status, paymentType, replaceFlagged } =
    filters;

  const transactionNo = useDebounce(filters.transactionNo, SEARCH_DEBOUNCE_MS);
  const taxId = useDebounce(filters.taxId, SEARCH_DEBOUNCE_MS);
  const customerName = useDebounce(filters.customerName, SEARCH_DEBOUNCE_MS);
  const customerCode = useDebounce(filters.customerCode, SEARCH_DEBOUNCE_MS);

  // A keystroke moves the list to page 1 at once, but its text reaches the request only after
  // the pause. The table waits for it, so the page change alone does not fetch the old search.
  const isTypingSearch =
    transactionNo !== filters.transactionNo ||
    taxId !== filters.taxId ||
    customerName !== filters.customerName ||
    customerCode !== filters.customerCode;

  const listFilter = useMemo(
    () =>
      toTransactionListFilter({
        dateFrom,
        dateTo,
        companyId,
        routeCode,
        transactionNo,
        status,
        paymentType,
        taxId,
        customerName,
        customerCode,
        replaceFlagged,
      }),
    [
      dateFrom,
      dateTo,
      companyId,
      routeCode,
      transactionNo,
      status,
      paymentType,
      taxId,
      customerName,
      customerCode,
      replaceFlagged,
    ],
  );

  return (
    <Paper
      sx={{
        padding: "24px 16px 16px",
        [compact]: { padding: "12px 12px 12px" },
      }}
    >
      <SalesFilter filters={filters} onChange={changeFilters} />
      <TransactionsTable
        listFilter={listFilter}
        isTypingSearch={isTypingSearch}
        triggerFetch={triggerFetch}
        page={page}
        setPage={setPage}
        limit={limit}
        setLimit={setLimit}
        listSearch={listSearch}
      />
    </Paper>
  );
}
